import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet as WalletIcon, ArrowUpRight, ShieldCheck, Bitcoin, Send } from "lucide-react";
import { useUser } from "../context/UserContext";
import { getWallet, withdrawLtc } from "../lib/api";
import { formatEUR, timeAgo } from "../lib/constants";
import { toast } from "sonner";

export default function Wallet() {
  const { user } = useUser();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const load = () => {
    setLoading(true);
    getWallet().then(setWallet).catch(() => setWallet(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [user?.id]);

  if (user?.role !== "creator") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="cc-overline">Wallet</div>
        <h1 className="text-3xl font-semibold text-white mt-2 mb-6">Creator-only</h1>
        <div className="cc-surface rounded-2xl p-6 text-sm text-[#a0a0b0]">
          Companies manage billing from the <Link to="/payments" className="text-[#00ff88] hover:underline">Payments</Link> page.
        </div>
      </div>
    );
  }
  if (loading) return <div className="max-w-5xl mx-auto px-6 py-12 text-[#606070]">Loading…</div>;
  if (!wallet) return <div className="max-w-5xl mx-auto px-6 py-12 text-white">Wallet unavailable.</div>;

  const minWithdraw = wallet.min_withdraw_usd || 20;
  const available = wallet.available_balance || 0;
  const pending = wallet.pending_balance || 0;
  const belowMin = available < minWithdraw;
  const totalEarned = available + pending
    + (wallet.withdrawals || []).reduce((a, w) => a + (w.amount || 0), 0);

  return (
    <div data-testid="wallet-page" className="max-w-7xl mx-auto px-6 py-12">
      <div className="cc-overline">Finance</div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-2 mb-10">Wallet</h1>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <BalanceCard
          testId="balance-available"
          label="Available · withdrawable"
          value={formatEUR(available)}
          sub={belowMin ? `Min $${minWithdraw} to withdraw` : "Ready to send to your LTC wallet"}
          icon={WalletIcon}
          accent
          action={
            <button
              data-testid="withdraw-btn"
              onClick={() => setShowWithdraw(true)}
              disabled={belowMin}
              className="cc-btn-primary rounded-lg px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <Send size={12} /> Send to LTC wallet
            </button>
          }
        />
        <BalanceCard
          testId="balance-pending"
          label="Pending · in escrow"
          value={formatEUR(pending)}
          sub="Unlocks when the buyer approves"
          icon={ShieldCheck}
          amber
        />
        <BalanceCard
          testId="balance-earned"
          label="Total earned"
          value={formatEUR(totalEarned)}
          sub="100% — buyer pays the platform fee"
          icon={ArrowUpRight}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="cc-surface rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#15152a] flex items-center justify-between">
            <div className="cc-overline">Transaction history</div>
            <span className="text-[11px] text-[#606070]">{(wallet.transactions || []).length} entries</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#606070] border-b border-[#15152a]">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(wallet.transactions || []).length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-[#606070]">No transactions.</td></tr>
              )}
              {(wallet.transactions || []).map((t) => (
                <tr key={t.id} data-testid={`wallet-tx-${t.id}`} className="border-b border-[#15152a] hover:bg-[#0a0a14]/50">
                  <td className="px-5 py-3 text-[11px] text-[#606070]">{timeAgo(t.created_at)}</td>
                  <td className="px-5 py-3 font-mono text-[#00ff88]">{t.order_id || "—"}</td>
                  <td className="px-5 py-3"><TxTypeBadge type={t.type} /></td>
                  <td className={`px-5 py-3 text-right font-mono ${t.amount < 0 ? "text-[#ff4444]" : "text-white"}`}>${t.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="cc-surface rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#15152a] flex items-center justify-between">
            <div className="cc-overline">Withdrawals</div>
            <span className="text-[11px] text-[#606070]">{(wallet.withdrawals || []).length} entries</span>
          </div>
          <div className="divide-y divide-[#15152a]">
            {(wallet.withdrawals || []).length === 0 && (
              <div className="px-5 py-8 text-center text-[#606070] text-sm">No withdrawals yet.</div>
            )}
            {(wallet.withdrawals || []).map((w) => (
              <div key={w.id} data-testid={`withdraw-row-${w.id}`} className="px-5 py-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-lg bg-[#06060c] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
                    <Bitcoin size={14} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-white">
                      LTC · <span className="font-mono text-xs text-[#a0a0b0]">{w.destination?.slice(0, 12)}…{w.destination?.slice(-6)}</span>
                    </div>
                    <div className="text-[10px] text-[#606070] uppercase tracking-widest">{timeAgo(w.created_at)}</div>
                    {w.tx_hash && (
                      <a
                        href={`https://litecoinspace.org/tx/${w.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] font-mono text-[#00ff88] hover:underline"
                      >
                        tx {w.tx_hash.slice(0, 10)}…
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm text-white">${w.amount}</div>
                  {w.amount_ltc && <div className="text-[10px] font-mono text-[#a0a0b0]">≈ {Number(w.amount_ltc).toFixed(6)} Ł</div>}
                  <div className={`text-[10px] uppercase tracking-widest ${w.status === "finished" ? "text-[#00ff88]" : w.status === "failed" ? "text-[#ff4444]" : "text-[#00bbff]"}`}>
                    {w.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {showWithdraw && (
        <WithdrawDialog
          available={available}
          minAmount={minWithdraw}
          onClose={() => setShowWithdraw(false)}
          onDone={() => { setShowWithdraw(false); load(); }}
        />
      )}
    </div>
  );
}

const TxTypeBadge = ({ type }) => {
  if (type === "escrow_hold") return <span className="cc-chip text-[#ffaa00] border-[#ffaa00]/30">escrow hold</span>;
  if (type === "released") return <span className="cc-chip text-[#00ff88] border-[#00ff88]/30">released</span>;
  if (type === "refund") return <span className="cc-chip text-[#ff4444] border-[#ff4444]/30">refunded</span>;
  if (type === "withdrawal") return <span className="cc-chip text-[#00bbff] border-[#00bbff]/30">withdrawal</span>;
  return <span className="cc-chip">{type}</span>;
};

const BalanceCard = ({ label, value, sub, icon: Icon, accent, amber, action, testId }) => (
  <div data-testid={testId} className="cc-surface cc-card-hover rounded-2xl p-7 flex flex-col gap-3">
    <div className="flex items-start justify-between">
      <div>
        <div className="cc-overline">{label}</div>
        <div className={`font-mono text-3xl mt-3 ${accent ? "text-[#00ff88]" : amber ? "text-[#ffaa00]" : "text-white"}`}>{value}</div>
        <div className="text-[11px] text-[#606070] mt-1">{sub}</div>
      </div>
      <span className="w-9 h-9 rounded-lg bg-[#06060c] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
        <Icon size={16} />
      </span>
    </div>
    {action && <div className="mt-2">{action}</div>}
  </div>
);

const WithdrawDialog = ({ available, minAmount, onClose, onDone }) => {
  const [amount, setAmount] = useState(Math.max(minAmount, Math.min(available, 50)));
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const valid = address.trim().length > 20 && /^(L|M|ltc1)/.test(address.trim());

  const submit = async () => {
    if (amount < minAmount) { toast.error(`Minimum $${minAmount}`); return; }
    if (amount > available) { toast.error("Insufficient balance"); return; }
    if (!valid) { toast.error("Enter a valid LTC address (starts with L, M or ltc1)"); return; }
    setSaving(true);
    try {
      const res = await withdrawLtc({ amount, address: address.trim() });
      toast.success(`Sent · ${Number(res.amount_ltc || 0).toFixed(6)} Ł on the way`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="withdraw-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-md">
        <div className="cc-overline">Withdraw to LTC wallet</div>
        <h3 className="text-xl font-semibold text-white mt-1 mb-4">
          Available: <span className="text-[#00ff88] font-mono">${available}</span>
        </h3>

        <div className="space-y-4">
          <div>
            <div className="cc-overline mb-1">Amount ($, min {minAmount})</div>
            <input
              data-testid="withdraw-amount"
              type="number"
              min={minAmount}
              max={available}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
            />
          </div>

          <div>
            <div className="cc-overline mb-1 inline-flex items-center gap-1.5">
              <Bitcoin size={11} className="text-[#00ff88]" /> LTC wallet address
            </div>
            <input
              data-testid="withdraw-ltc-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="L… or M… or ltc1…"
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
            />
            <div className="text-[10px] text-[#606070] mt-1">
              Your funds will be converted USD → LTC at the live rate and sent on-chain. Confirmation in ~2 minutes.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button
            data-testid="withdraw-submit"
            onClick={submit}
            disabled={saving || !valid || amount < minAmount || amount > available}
            className="cc-btn-primary rounded-lg px-5 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <Send size={12} /> {saving ? "Sending…" : `Send $${amount}`}
          </button>
        </div>
      </div>
    </div>
  );
};
